import { useState, useEffect, useCallback } from 'react';
import MdLicenseBanner from "../components/MdLicenseBanner";
import {
  Activity, AlertTriangle, Bell, CheckCircle, ChevronRight,
  Clock, HardDrive, Loader2, Monitor, Network, Play, Radio,
  RefreshCw, Router, ScanLine, Server, Shield, Wifi, XCircle,
  Zap, Camera, Lock, Smartphone, TrendingUp, Video, Link2, HelpCircle
} from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import monitoringTutorial from '../components/tutorials/configs/monitoringTutorial';
import { useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  RadialBarChart, RadialBar
} from 'recharts';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';
import useAuthStore from '../stores/authStore';

// ── Color palette (matches CCM V2 tokens) ──────────────────
const COLORS = {
  primary: '#00d4aa',
  secondary: '#0ea5e9',
  purple: '#a855f7',
  warning: '#f59e0b',
  danger: '#ef4444',
  success: '#22c55e',
  muted: '#64748b',
  pink: '#ec4899',
  cyan: '#06b6d4',
};

const DEVICE_COLORS = {
  CAMERA: COLORS.primary,
  NVR: COLORS.secondary,
  DVR: COLORS.secondary,
  SWITCH: COLORS.purple,
  ROUTER: COLORS.pink,
  VMS_SERVER: COLORS.cyan,
  SERVER: COLORS.warning,
  ACCESS_CONTROLLER: COLORS.success,
  INTERCOM: '#8b5cf6',
  UNKNOWN: COLORS.muted,
  ACCESS_POINT: COLORS.secondary,
  WORKSTATION: '#f97316',
  OTHER: COLORS.muted,
};

const STATUS_COLORS = {
  ONLINE: COLORS.success,
  OFFLINE: COLORS.danger,
  DEGRADED: COLORS.warning,
  UNKNOWN: COLORS.muted,
};

// ── Helpers ────────────────────────────────────────────────
const formatRelativeTime = (date) => {
  if (!date) return 'Never';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const formatDeviceType = (type) => {
  if (!type) return 'Unknown';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    .replace('Vms Server', 'VMS').replace('Nvr', 'NVR').replace('Dvr', 'DVR');
};

const DeviceTypeIcon = ({ type, size = 16 }) => {
  const icons = {
    CAMERA: Video, NVR: HardDrive, DVR: HardDrive, VMS_SERVER: Server,
    SERVER: Server, SWITCH: Network, ROUTER: Router, ACCESS_CONTROLLER: Lock,
    INTERCOM: Smartphone, ACCESS_POINT: Wifi, WORKSTATION: Monitor,
    UNKNOWN: Monitor,
  };
  const Icon = icons[type] || Monitor;
  return <Icon size={size} />;
};

function formatBps(bps) {
  if (!bps || bps === 0) return '0 bps';
  if (bps >= 1e9) return `${(bps / 1e9).toFixed(1)} Gbps`;
  if (bps >= 1e6) return `${(bps / 1e6).toFixed(1)} Mbps`;
  if (bps >= 1e3) return `${(bps / 1e3).toFixed(0)} Kbps`;
  return `${bps} bps`;
}

// ── Custom Recharts Tooltip ────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="md-chart-tooltip">
      <div className="md-chart-tooltip-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="md-chart-tooltip-item" style={{ color: p.color }}>
          <span className="md-chart-tooltip-dot" style={{ background: p.color }} />
          {p.name}: <strong>{typeof p.value === 'number' ? p.value.toFixed(1) : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

// ── Severity Badge ─────────────────────────────────────────
const SeverityBadge = ({ severity }) => {
  const config = {
    CRITICAL: { color: COLORS.danger, icon: XCircle },
    WARNING: { color: COLORS.warning, icon: AlertTriangle },
    INFO: { color: COLORS.secondary, icon: Bell },
  };
  const { color, icon: Icon } = config[severity] || config.INFO;
  return (
    <span className="md-severity-badge" style={{ background: `${color}18`, color, borderColor: `${color}30` }}>
      <Icon size={13} /> {severity}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN DASHBOARD COMPONENT
// ═══════════════════════════════════════════════════════════
const MdDashboard = () => {
  const navigate = useNavigate();
  const { tenantVersion } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [devices, setDevices] = useState([]);
  const [agents, setAgents] = useState([]);
  // Which agent runs discovery scans. HOST_COLLECTOR agents are excluded
  // entirely -- they must never appear as a scan target.
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [healthPercent, setHealthPercent] = useState(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [latencyData, setLatencyData] = useState([]);
  const [throughputData, setThroughputData] = useState({ totalBps: 0, history: [], switches: [] });
  const [noAccess, setNoAccess] = useState(false);

  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(monitoringTutorial);

  // Reset on tenant switch
  useEffect(() => {
    setDevices([]); setAgents([]); setAlerts([]); setLatencyData([]); setLoading(true); setNoAccess(false);
  }, [tenantVersion]);


  // ── Fetch all dashboard data ──────────────────────────
  const fetchData = useCallback(async () => {
    try {
      // Try consolidated dashboard endpoint first
      const dashRes = await fetchWithTenant('/api/md/dashboard');
      if (dashRes.ok) {
        const data = await dashRes.json();
        setDevices(Array.isArray(data.devices) ? data.devices : []);
        setAgents(Array.isArray(data.agents) ? data.agents : []);
        setAlerts(Array.isArray(data.alerts) ? data.alerts : []);
        setHealthPercent(typeof data.healthPercent === 'number' ? data.healthPercent : null);
        setNoAccess(false);
      } else if (dashRes.status === 403) {
        setDevices([]); setAgents([]); setAlerts([]); setLatencyData([]);
        setNoAccess(true);
        return;
      } else {
        // Fallback: fetch individually
        const [devRes, agentRes] = await Promise.all([
          fetchWithTenant('/api/md/devices'),
          fetchWithTenant('/api/md/agents'),
        ]);
        if (devRes.status === 403 || agentRes.status === 403) {
          setDevices([]); setAgents([]); setAlerts([]); setLatencyData([]);
          setNoAccess(true);
          return;
        }
        setNoAccess(false);
        if (devRes.ok) {
          const d = await devRes.json();
          setDevices(d.devices || d || []);
        }
        if (agentRes.ok) {
          const a = await agentRes.json();
          setAgents(a || []);
        }
      }

      // Fetch real network throughput data
      try {
        const tpRes = await fetchWithTenant('/api/md/switches/aggregate/throughput?hours=1&points=30');
        if (tpRes.ok) {
          const tp = await tpRes.json();
          setThroughputData(tp);
          setLatencyData((tp.history || []).map(h => ({
            time: new Date(h.t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            tx: h.txBps || 0,
            rx: h.rxBps || 0,
          })));
        }
      } catch { /* best-effort */ }
    } catch (error) {
      console.error('[MD Dashboard] Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantVersion]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Agents eligible to run a scan -- HOST_COLLECTOR agents must never appear
  // as a scan target, not just be blocked server-side.
  const eligibleAgents = agents.filter(a => a.role !== 'HOST_COLLECTOR');
  const selectedAgent = eligibleAgents.find(a => a.id === selectedAgentId) || null;
  const isSelectedAgentOnline = !!selectedAgent && (selectedAgent.isOnline || selectedAgent.status === 'ONLINE');

  // Default-select the first available (online, non-HOST_COLLECTOR) agent once
  // agents load, so existing single-agent tenants see no behavior change. Left
  // alone once a valid selection exists so refetches don't clobber a manual pick.
  useEffect(() => {
    if (selectedAgentId && eligibleAgents.some(a => a.id === selectedAgentId)) return;
    const defaultAgent = eligibleAgents.find(a => a.isOnline || a.status === 'ONLINE') || eligibleAgents[0] || null;
    setSelectedAgentId(defaultAgent ? defaultAgent.id : '');
  }, [agents]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Trigger scan ──────────────────────────────────────
  const triggerScan = async () => {
    if (!selectedAgent) {
      toast.error('Select an agent to scan with');
      return;
    }
    if (!isSelectedAgentOnline) {
      toast.error(`${selectedAgent.name} is not currently online`);
      return;
    }
    setScanning(true);
    try {
      const res = await fetchWithTenant('/api/md/scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId: selectedAgent.id }),
      });
      if (res.ok) {
        toast.success('Network scan started...');
        setTimeout(fetchData, 15000);
        setTimeout(fetchData, 30000);
        setTimeout(() => { fetchData(); setScanning(false); toast.success('Scan complete!'); }, 45000);
      } else {
        toast.error('Failed to start scan');
        setScanning(false);
      }
    } catch {
      toast.error('Scan request failed');
      setScanning(false);
    }
  };

  // ── Derived stats ─────────────────────────────────────
  const onlineDevices = devices.filter(d => d.status === 'ONLINE').length;
  const offlineDevices = devices.filter(d => d.status === 'OFFLINE').length;
  const degradedDevices = devices.filter(d => d.status === 'DEGRADED').length;
  // Scan-capable agents only -- HOST_COLLECTOR agents don't count here.
  const onlineAgents = eligibleAgents.filter(a => a.isOnline || a.status === 'ONLINE').length;
  const activeAlerts = alerts.filter(a => !a.acknowledgedAt).length;

  // Agent picker options -- label shows name + IP (falling back to hostname,
  // then just the name), suffixed with online/offline status.
  const agentOptions = eligibleAgents.map(a => {
    const online = a.isOnline || a.status === 'ONLINE';
    const addr = a.localIp || a.hostname;
    const label = addr ? `${a.name} (${addr})` : a.name;
    return (
      <option key={a.id} value={a.id}>{label} — {online ? 'Online' : 'Offline'}</option>
    );
  });

  // healthPercent comes from the API; null = UNKNOWN (no monitored devices or all UNKNOWN).
  // Never show a false 0%.
  const healthKnown = healthPercent !== null && healthPercent !== undefined;
  const healthDisplay = healthKnown ? healthPercent : 0;
  const healthColor = !healthKnown
    ? COLORS.muted
    : healthDisplay >= 90 ? COLORS.success
    : healthDisplay >= 70 ? COLORS.warning
    : COLORS.danger;

  // Device type breakdown for pie chart
  const typeCount = {};
  devices.forEach(d => {
    const t = d.deviceType || 'UNKNOWN';
    typeCount[t] = (typeCount[t] || 0) + 1;
  });
  const devicesByType = Object.entries(typeCount)
    .map(([name, value]) => ({ name: formatDeviceType(name), value, color: DEVICE_COLORS[name] || COLORS.muted }))
    .sort((a, b) => b.value - a.value);

  // Status breakdown
  const statusBreakdown = [
    { name: 'Online', value: onlineDevices, color: COLORS.success },
    { name: 'Offline', value: offlineDevices, color: COLORS.danger },
    { name: 'Degraded', value: degradedDevices, color: COLORS.warning },
  ];

  // Recent activity (devices sorted by last seen)
  const recentDevices = [...devices]
    .sort((a, b) => new Date(b.lastSeenAt || 0) - new Date(a.lastSeenAt || 0))
    .slice(0, 6);

  // Health gauge data for RadialBarChart
  const gaugeData = [{ value: healthDisplay, fill: healthColor }];

  // ── Loading state ─────────────────────────────────────
  if (loading) {
    return (
      <div className="md-loading">
        <Loader2 size={32} className="md-spin" />
        <span>Loading dashboard...</span>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════
  return (
    <div className="md-page">
      {/* Header */}
      <div className="page-header-with-actions">
        <div className="page-header-content">
          <h1 className="page-title">
            <Activity size={22} />
            Monitoring Dashboard
          </h1>
          <p className="page-subtitle">Real-time overview of your security infrastructure</p>
        </div>
        <div className="page-header-actions">
          <button className="tutorial-help-btn" onClick={() => startTutorial(monitoringTutorial.module, monitoringTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
          <button className="btn btn-secondary" onClick={fetchData}>
            <RefreshCw size={15} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={triggerScan} disabled={scanning}>
            {scanning ? <Loader2 size={15} className="md-spin" /> : <ScanLine size={15} />}
            {scanning ? 'Scanning...' : 'Run Discovery'}
          </button>
        </div>
      </div>

      <MdLicenseBanner />
      {/* Agent Banner */}
      {agents.length > 0 && (
        <div className={`md-agent-banner ${onlineAgents > 0 ? 'online' : 'offline'}`}>
          <div className="md-agent-banner-left">
            <Radio size={16} />
            <span className="md-agent-banner-status">
              {onlineAgents > 0 ? `${onlineAgents} Agent${onlineAgents > 1 ? 's' : ''} Online` : 'All Agents Offline'}
            </span>
            <span className="md-agent-banner-name">
              {agents.map(a => a.name).join(', ')}
            </span>
          </div>
          {eligibleAgents.length > 0 && (
            <select
              className="form-select"
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              title="Select which agent runs discovery"
            >
              {agentOptions}
            </select>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => navigate("/monitoring/agents")}>
            Manage <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* ── STAT CARDS ─────────────────────────────────── */}
      <div className="md-stat-grid">
        <div className="md-stat-card">
          <div className="md-stat-icon" style={{ background: `${COLORS.primary}15` }}>
            <Monitor size={22} color={COLORS.primary} />
          </div>
          <div className="md-stat-content">
            <span className="md-stat-label">Total Devices</span>
            <span className="md-stat-value">{devices.length}</span>
            <span className="md-stat-sub">across {agents.length} agent{agents.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        <div className="md-stat-card">
          <div className="md-stat-icon" style={{ background: `${COLORS.success}15` }}>
            <CheckCircle size={22} color={COLORS.success} />
          </div>
          <div className="md-stat-content">
            <span className="md-stat-label">Online</span>
            <span className="md-stat-value">{onlineDevices}<span className="md-stat-total">/{devices.length}</span></span>
            <span className="md-stat-sub">{offlineDevices} offline, {degradedDevices} degraded</span>
          </div>
        </div>

        <div className="md-stat-card">
          <div className="md-stat-icon" style={{ background: `${COLORS.danger}15` }}>
            <AlertTriangle size={22} color={COLORS.danger} />
          </div>
          <div className="md-stat-content">
            <span className="md-stat-label">Active Alerts</span>
            <span className="md-stat-value">{activeAlerts}</span>
            <span className="md-stat-sub">{alerts.length} total</span>
          </div>
        </div>

        <div className="md-stat-card">
          <div className="md-stat-icon" style={{ background: `${COLORS.secondary}15` }}>
            <Radio size={22} color={COLORS.secondary} />
          </div>
          <div className="md-stat-content">
            <span className="md-stat-label">Agents</span>
            <span className="md-stat-value">{onlineAgents}<span className="md-stat-total">/{agents.length}</span></span>
            <span className="md-stat-sub">{onlineAgents} online, {agents.length - onlineAgents} offline</span>
          </div>
        </div>
      </div>

      {/* ── HEALTH GAUGE + CHARTS ROW ──────────────────── */}
      <div className="md-charts-row">
        {/* Health Gauge */}
        <div className="md-chart-card md-health-gauge-card">
          <div className="md-health-gauge-wrapper">
            <ResponsiveContainer width="100%" height={120}>
              <RadialBarChart cx="50%" cy="90%" innerRadius="70%" outerRadius="100%" startAngle={180} endAngle={0} data={gaugeData} barSize={12}>
                <RadialBar background={{ fill: '#1a2332' }} dataKey="value" cornerRadius={6} max={100} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="md-health-gauge-label">
              <span className="md-health-gauge-value" style={{ color: gaugeData[0].fill }}>
                {healthKnown ? healthPercent + '%' : '—'}
              </span>
              <span className="md-health-gauge-text">
                {healthKnown ? 'System Health' : 'No devices monitored'}
              </span>
            </div>
          </div>
          <div className="md-health-status-row">
            {statusBreakdown.map(s => (
              <div key={s.name} className="md-health-status-item">
                <span className="md-health-status-value" style={{ color: s.color }}>{s.value}</span>
                <span className="md-health-status-label">{s.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Network Throughput Chart */}
        <div className="md-chart-card">
          <div className="md-chart-header">
            <span className="md-chart-title">Network Throughput</span>
            <span className="md-chart-subtitle">
              {(throughputData.history && throughputData.history.length > 0)
                ? formatBps(throughputData.totalBps || 0) + ' total'
                : 'No data'}
            </span>
          </div>
          <div className="md-chart-body">
            {(throughputData.history && throughputData.history.length > 0) ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={latencyData}>
                <defs>
                  <linearGradient id="lgTx" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="lgRx" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2d3d" />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} interval={7} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} tickFormatter={v => formatBps(v)} width={52} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="tx" stroke="#3B82F6" fill="url(#lgTx)" strokeWidth={2} name="TX" dot={false} />
                <Area type="monotone" dataKey="rx" stroke="#10B981" fill="url(#lgRx)" strokeWidth={2} name="RX" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
            ) : (
              <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8, color: "#64748b" }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>No switch traffic data</div>
                <div style={{ fontSize: 11, opacity: 0.7 }}>Check switch credentials or device status</div>
              </div>
            )}
          </div>
        </div>

        {/* Device Type Pie */}
        <div className="md-chart-card">
          <div className="md-chart-header">
            <span className="md-chart-title">Device Types</span>
            <span className="md-chart-subtitle">{devices.length} total</span>
          </div>
          <div className="md-chart-body">
            {devicesByType.length > 0 ? (
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={devicesByType} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value" stroke="none">
                    {devicesByType.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="md-chart-empty">No devices discovered</div>
            )}
          </div>
          <div className="md-chart-legend">
            {devicesByType.map(d => (
              <span key={d.name} className="md-legend-item">
                <span className="md-legend-dot" style={{ background: d.color }} />
                {d.name} ({d.value})
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── RECENT ALERTS + RECENT DEVICES ─────────────── */}
      <div className="md-two-col">
        {/* Recent Alerts */}
        <div className="md-panel">
          <div className="md-panel-header">
            <span className="md-panel-title">Recent Alerts</span>
            <button className="md-panel-link" onClick={() => navigate('/monitoring/alerts')}>
              View All <ChevronRight size={14} />
            </button>
          </div>
          <div className="md-panel-body">
            {alerts.length > 0 ? alerts.slice(0, 5).map(a => (
              <div key={a.id} className="md-alert-row">
                <span className={`md-status-dot ${a.severity === 'CRITICAL' ? 'danger' : a.severity === 'WARNING' ? 'warning' : 'info'}`} />
                <div className="md-alert-row-content">
                  <span className="md-alert-row-title">{a.title}</span>
                  <span className="md-alert-row-time">{formatRelativeTime(a.triggeredAt)}</span>
                </div>
                <SeverityBadge severity={a.severity} />
              </div>
            )) : (
              <div className="md-panel-empty">
                <Bell size={20} />
                <span>No alerts — all systems nominal</span>
              </div>
            )}
          </div>
        </div>

        {/* Recently Seen Devices */}
        <div className="md-panel">
          <div className="md-panel-header">
            <span className="md-panel-title">Recently Active Devices</span>
            <button className="md-panel-link" onClick={() => navigate('/monitoring/discovery')}>
              View All <ChevronRight size={14} />
            </button>
          </div>
          <div className="md-panel-body">
            {recentDevices.length > 0 ? recentDevices.map(d => (
              <div key={d.id} className="md-device-row" onClick={() => navigate('/monitoring/discovery')}>
                <span className={`md-status-dot ${d.status === 'ONLINE' ? 'success' : d.status === 'OFFLINE' ? 'danger' : 'warning'}`} />
                <DeviceTypeIcon type={d.deviceType} size={16} />
                <div className="md-device-row-content">
                  <span className="md-device-row-name">{d.hostname || d.name || d.ipAddress}</span>
                  <span className="md-device-row-sub">{d.ipAddress} · {formatDeviceType(d.deviceType)}</span>
                </div>
                <span className="md-device-row-time">{formatRelativeTime(d.lastSeenAt)}</span>
              </div>
            )) : (
              <div className="md-panel-empty">
                <Monitor size={20} />
                <span>No devices discovered yet</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── QUICK ACTIONS ──────────────────────────────── */}
      <div className="md-quick-actions">
        <button className="md-quick-action" style={{ '--action-color': COLORS.primary }} onClick={() => navigate('/monitoring/agents')}>
          <Radio size={16} /> Pair Agent
        </button>
        <button className="md-quick-action" style={{ '--action-color': COLORS.secondary }} onClick={triggerScan} disabled={scanning}>
          <ScanLine size={16} /> Run Discovery
        </button>
        <button className="md-quick-action" style={{ '--action-color': COLORS.warning }} onClick={() => navigate('/monitoring/alerts')}>
          <Bell size={16} /> Alert Rules
        </button>
        <button className="md-quick-action" style={{ '--action-color': COLORS.purple }} onClick={() => navigate('/monitoring/discovery')}>
          <Link2 size={16} /> Link Integration
        </button>
      </div>

      {showPrompt && (
        <TutorialPrompt
          module={monitoringTutorial.module}
          moduleName={monitoringTutorial.moduleName}
          steps={monitoringTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default MdDashboard;
